import { useEffect } from 'react';
import { useAuthState } from './api.ts';
import type { AgentAction, Feeling, IssueSeverity, TestBehaviour, TestStatus, VisitStatus } from '@daevio/shared';

export const visitStatusLabel: Record<VisitStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  converted: 'Converted',
  satisfied: 'Got what they needed',
  abandoned: 'Left',
  interrupted: 'Stopped by our limits',
  failed: 'Failed',
  blocked: 'Blocked',
  login_wall: 'Stopped at sign-in',
  cancelled: 'Cancelled',
};

/** In an inspection the visitor who leaves has covered the site. */
export function statusLabel(status: VisitStatus, behaviour: TestBehaviour = 'human'): string {
  return behaviour === 'check' && status === 'satisfied' ? 'Inspected' : visitStatusLabel[status];
}

export const feelingLabel: Record<Feeling, string> = {
  neutral: 'Neutral',
  interested: 'Interested',
  reassured: 'Reassured',
  confused: 'Confused',
  annoyed: 'Annoyed',
  bored: 'Bored',
  stuck: 'Stuck',
};

/** Seconds as a person would say them: "2 min 10 s". */
export function formatSpent(ms: number): string {
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  return `${Math.floor(s / 60)} min${s % 60 ? ` ${s % 60} s` : ''}`;
}

export const testStatusLabel: Record<TestStatus, string> = {
  running: 'Running',
  reporting: 'Writing report',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const severityLabel: Record<IssueSeverity, string> = { low: 'Low', medium: 'Medium', high: 'High' };

export function actionLabel(action: AgentAction): string {
  switch (action.type) {
    case 'click':
      return `Click element ${action.element}`;
    case 'type':
      return `Type “${action.text}”${action.submit ? ' and press Enter' : ''}`;
    case 'select':
      return `Choose “${action.option}”`;
    case 'scroll':
      return `Scroll ${action.direction}`;
    case 'back':
      return 'Go back';
    case 'wait':
      return 'Wait';
    case 'leave':
      return action.goal === 'yes' ? 'Leave: got what they came for' : 'Leave the site';
    case 'done':
      return action.success ? 'Done: goal reached' : 'Give up';
  }
}

export function formatUsd(n: number): string {
  return n < 0.01 && n > 0 ? '< $0.01' : `$${n.toFixed(2)}`;
}

/**
 * AI costs in USD are for us, not for customers (who see visits): shown only in the internal
 * admin-token mode.
 */
export function useShowCosts(): boolean {
  return useAuthState().mode === 'admin-token';
}

/** Calls `reload` every `ms` while `active`. */
export function usePolling(active: boolean, reload: () => void, ms = 3000) {
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(reload, ms);
    return () => clearInterval(timer);
    // `reload` is a new closure on every render; only `active` should restart the timer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, ms]);
}
